import { DataType } from '../types/common';
import { pageSizeList } from './localization';

export const NEW_ITEM_ID = 'new';
export const IDP_NAVIGATION_DELAY_MS = 3000;
export const ENVIRONMENT_COLORS = [
    '#EDEAE0', '#FFBF00', '#9966CC', '#007FFF', '#50C878', '#E0115F', '#708090', '#40E0D0'
];
export const TABLE_ROWS_OPTIONS = pageSizeList.map(page => page.code);
export const ROWS_PER_PAGE = 10;
export const M3_USER_ID = 'm3UserId';
export const FIRST_NAME = 'firstName';
export const LAST_NAME = 'lastName';

export const FOLDER_ICON = 'pi pi-folder';
export const FILE_ICON = 'pi pi-file';

export const FILE_TYPES_ICONS = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'pi pi-file-excel',
    'image/png': 'pi pi-image',
    'application/pdf': 'pi pi-file-pdf'
} as Record<string, string>;

export const DATATYPE_ICONS = {
    [DataType.DATE]: 'pi pi-calendar-clock',
    [DataType.NUMERIC]: 'pi pi-calculator',
    [DataType.STRING]: 'pi pi-align-left',
    [DataType.BOOLEAN]: 'pi pi-check-square',
    [DataType.MESSAGE]: 'pi pi-message'
};

export const identityProviderOptions = [
    { value: 'ms365', label: 'Microsoft' },
    { value: 'okta', label: 'Okta' }
];

export const SCIM_API_BASE_URL = `${import.meta.env.VITE_VL_API_BASE_URL}/v1/scim`;

// Enum for permission types
export enum PermissionType {
    VINCE_LIVE_ADMIN = 'VinceLiveAdmin',
    VINCE_LIVE_USER = 'VinceLiveUser',
    TENANT_ADMIN = 'TenantAdmin',
    TENANT_USER = 'TenantUser'
  }

// Enum for permission display text
export enum PermissionText {
    VINCE_LIVE_ADMIN = 'VINCE_LIVE_ADMIN',
    VINCE_LIVE_USER = 'VINCE_LIVE_USER',
    TENANT_ADMIN = 'TENANT_ADMIN',
    TENANT_USER = 'TENANT_USER'
}

export const EXCLUDE_CREATED_KEY = 'created';
export const EXCLUDE_MODIFIED_KEY = 'modified';
export const API_CLIENT_NAME_REGEX = /^[a-zA-Z0-9 +=,.@-]+$/;

export const ExceptionErrors = {
    USER_NOT_FOUND: 'UserNotFoundException',
    NOT_AUTHORIZED: 'NotAuthorizedException',
    CODE_MISMATCH: 'CodeMismatchException',
    LIMIT_EXCEEDED: 'LimitExceededException',
    INVALID_PARAMETER: 'InvalidParameterException',
    USER_DISABLED: 'User is disabled.'
};

export enum ForgotStep {
    RequestCode = 'RequestCode',
    EnterCodeAndReset = 'EnterCodeAndReset'
}

/**
 * Represents a collection of foundational resources used within the application.
 * Each resource is represented as an object containing a unique `value`,
 * a descriptive `text` identifier, and optionally, an `entityName` property.
 *
 * - `value`: A unique identifier for the resource.
 * - `text`: A textual reference used for resource localization or representation.
 * - `entityName`: The associated entity name in DynamoDB for the resource.
 */
export const FOUNDATION_RESOURCES = [
    { value: 'apiclient', text: 'foundation.resource.apiclient', entityName: 'ApiClient' },
    { value: 'connection', text: 'foundation.resource.connection', entityName: 'Connection' },
    { value: 'dashboard', text: 'foundation.resource.dashboard', entityName: 'Dashboard' },
    { value: 'environment', text: 'foundation.resource.environment', entityName: 'Environment' },
    { value: 'user', text: 'foundation.resource.user', entityName: 'User' },
    { value: 'workflow', text: 'foundation.resource.workflow', entityName: 'Workflow' },
    { value: 'group', text: 'foundation.resource.group', entityName: 'Group' },
    { value: 'variable', text: 'foundation.resource.variable', entityName: 'Variable' },
    { value: 'webhook', text: 'foundation.resource.webhook', entityName: 'Webhook' },
    { value: 'metadata', text: 'foundation.resource.metadata', entityName: null },
    { value: 'gateway', text: 'foundation.resource.gateway', entityName: 'Gateway' }
];

export const CORE_APPS = [
    { value: 'customtables', text: 'core.app.customtables' },
    { value: 'fnd', text: 'core.app.fnd' }
];

export const ADDON_APPS = [
    { value: 'ai-apps', text: 'addon.app.aiApps' },
    { value: 'kanban', text: 'addon.app.kanban' },
    { value: 'campaign', text: 'addon.app.campaign' },
    { value: 'impex-helper', text: 'addon.app.impexHelper' },
    { value: 'mdbreadmi-helper', text: 'addon.app.mdbreadmiHelper' },
    { value: 'mi-test', text: 'addon.app.miTest' },
    { value: 'm3-printout-config', text: 'addon.app.m3PrintoutConfig' },
    { value: 'vince-pricer', text: 'addon.app.vincePricer' },
    { value: 'cugex-helper', text: 'addon.app.cugexHelper' },
    { value: 'isLantmannen', text: 'addon.app.isLantmannen' },
    { value: 'spykar', text: 'addon.app.spykar' },
    { value: 'eshopbox', text: 'addon.app.eshopbox' },
    { value: 'scanner-pick-pack', text: 'addon.app.scannerPickPack' },
    { value: 'pick-pack', text: 'addon.app.pickPack' },
    { value: 'contract-management', text: 'addon.app.contractManagement' }
];

export const WILDCARD_OPTION = { value: '*', text: 'wildcard.option' };

export const ACTION_OPTIONS = [
    { value: '*', text: 'wildcard.option' },
    { value: 'READ', text: 'action.read' },
    { value: 'RUN', text: 'action.run' },
    { value: 'WRITE', text: 'action.write' },
    { value: 'DEPLOY', text: 'action.deploy' },
    { value: 'DENY', text: 'action.deny' }
];

export const CUSTOM_TABLE_RESOURCES = [
    { value: 'meta', text: 'customTable.resource.configuration' },
    { value: 'data', text: 'customTable.resource.data' }
];

export const AI_APPS_RESOURCES = [
    { value: 'app', text: 'ai-apps.resource.app' }
];

export const TENANT_RESOURCES = [
    { value: 'tenant', text: 'tenants.resource.tenant' }
];

export const URL_LATEST_UPDATES = 'https://vincesoftware.com/product-documentation/release-notes';
export const URL_DOCUMENTATION = 'https://vincesoftware.com/product-documentation';
export const URL_SUPPORT = 'https://vincesoftware.com/customer-portal';
export const URL_NEWS = 'https://vincesoftware.com';
export const URL_FEEDBACK = 'https://vincesoftware.com/customer-portal?hsLang=en';

export const NO_SUBHEADER_KEY = '__no_subheader__';
export const OTHER_GROUP_KEY = 'Other';
export const BLANK_TARGET = '_blank';

export const DEFAULT_TENANT_NAME = 'labs';
export const APPS_PATH = '/apps';

export const SYSTEM_RESOURCES = {
    apiClients: {
        internalApiClient: 'InternalApiClient'
    },
    workflows: {
        m3ProgramsLookup: 'M3ProgramsLookup',
        m3MetadataImport: 'M3MetadataImport',
        m3ProxyService: 'SysM3APIProxyService'
    }
};

export const SELECTABLE_DATA_TYPES = Object.keys(DataType).filter(it => (it as DataType) !== DataType.MESSAGE);

export const CERTIFICATE_OPTIONS = [
    { label: 'connections.certificate.VinceLiveCertificate', value: 'VL_CERT' },
    { label: 'connections.certificate.CustomCertificate', value: 'CUSTOM_CERT' }
];

export const PARTNER_DOMAINS = [
    'anthesis.co.uk',
    'doppiogroup.com',
    'mpa2c.com',
    'navcite.se',
    'navcite.com',
    'meridion.se',
    'elvenite.se',
    'elvenite.com',
    'columbusglobal.com',
    'oceano-consulting.com',
    'integrationwizards.com',
    'i3consulting.co.nz',
    'authentic-grp.com',
    'trilis-c.com',
    'acterconsulting.com',
    'horsa.it',
    'horsa.com',
    'cicom.it',
    'integrationwizards.onmicrosoft.com',
    'mashfrog.com',
    'wipro.com',
    'be-terna.com',
    'erpkonsult.no',
    'mazeppa.no',
    'fortude.co'
];

export const PATH_VIEW = '/view';
export const PATH_EDIT = '/edit';
export const PATH_NEW = '/new';

export const PAGE_HEAD = {
    DEFAULT_TITLE_PREFIX: 'Vince Live',
    DEFAULT_FALLBACK_TITLE: 'Home',
    EXCLUDED_SEGMENTS: ['edit', 'view', 'new'],
    ID_DETAILS_TITLE: 'Details'
};

// Matches Vince resource IDs (e.g., ABC-123e4567e89b12d3a456426655440000)
export const REGEX_PATTERNS = {
    RESOURCE_ID: /^[A-Z]+-[0-9a-f]{32}$/i
};

// Regex to match M3 date format (YYYYMMDD or 8 numbers)
export const M3_DATE_FORMAT = /^\d{8}$/;

// Status filter option
export enum EntityStatusFilter {
    ALL = 'all',
    ACTIVE = 'active',
    DEACTIVATED = 'deactivated'
}

export const TIMEZONE_LOCALE = 'en-US';
export const TIMEZONE_NAME_FORMAT = 'shortOffset';
export const GMT_LABEL = 'GMT';
export const DEFAULT_UTC_OFFSET = 'UTC+00:00';
export const GMT_OFFSET_REGEX = /GMT([+-])(\d+):?(\d*)/;

export const ACCESS_MANAGEMENT_PATH = '/access-management';

// date-fns format string for M3 compact date (YYYYMMDD)
export const M3_DATE_FORMAT_STRING = 'yyyyMMdd';

// Ordered largest-to-smallest; formatDateTimePassed shows the largest non-zero unit plus the next one down
export const DURATION_UNIT_ORDER = ['years', 'months', 'days', 'hours', 'minutes', 'seconds'] as const;
export type DurationUnit = typeof DURATION_UNIT_ORDER[number];

const EN_DURATION_UNIT_LABELS: Record<DurationUnit, string> = { years: 'y', months: 'mo', days: 'd', hours: 'h', minutes: 'm', seconds: 's' };

// Short unit labels for formatDateTimePassed, keyed by language code (see languageList). Falls back to `en`.
export const DURATION_UNIT_LABELS: Record<string, Record<DurationUnit, string>> = {
    en: EN_DURATION_UNIT_LABELS,
    no: { years: 'å', months: 'mnd', days: 'd', hours: 't', minutes: 'm', seconds: 's' },
    fr: { years: 'a', months: 'mo', days: 'j', hours: 'h', minutes: 'm', seconds: 's' },
    sv: { years: 'å', months: 'mån', days: 'd', hours: 't', minutes: 'm', seconds: 's' }
};

// Always-defined fallback, used when `locale` is missing or unsupported (avoids indexed-access-possibly-undefined at call sites)
export const DEFAULT_DURATION_UNIT_LABELS = EN_DURATION_UNIT_LABELS;

export const PROMISE_STATUS = {
    FULFILLED: 'fulfilled',
    REJECTED: 'rejected'
} as const;
